import { computed, inject, linkedSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { signalStore } from '@tvd/cdk/store';
import {
  type BranchCreate,
  type BranchUpdate,
  localeToCountry,
} from '@shared/models/branch';
import { finalize, first, map, switchMap, tap } from 'rxjs';
import { AuthUser } from '../auth';
import { BranchApi } from './branch-api';

const initState = {
  loading: false,
};

export const BranchStore = signalStore(initState).create((state, patch) => {
  const branchApi = inject(BranchApi);
  const user = inject(AuthUser);

  const orgIds$ = toObservable(user.data).pipe(
    first((user) => !!user),
    map((user) => user?.roles?.map((role) => role.organisation) ?? []),
  );

  patch({
    loading: true,
  });

  const branches$ = orgIds$.pipe(
    switchMap((orgIds) =>
      branchApi
        .getBranches()
        .pipe(
          map((res) =>
            res.items.filter((branch) => orgIds.includes(branch.organisation)),
          ),
        ),
    ),
    finalize(() => patch({ loading: false })),
  );

  const branches = linkedSignal(toSignal(branches$, { initialValue: [] }));

  return {
    loading: computed(() => state().loading),
    branches: branches.asReadonly(),
    getBranch: (branchId: string) =>
      computed(() => branches().find((b) => b.id === branchId)),
    getBranches: (orgId: string) => {
      patch({ loading: true });
      return branchApi.getBranchesByOrgId(orgId).pipe(
        map((res) => res.items),
        tap((list) => branches.set(list)),
        finalize(() => patch({ loading: false })),
      );
    },
    createBranch: (branch: BranchCreate) => {
      patch({ loading: true });
      // Ensure a default country code is set when creating a branch
      const userLocale = user.data()?.locale || 'en-GB';

      const defaultCountry = localeToCountry[userLocale] || 'DE';

      const branchWithCountry: BranchCreate = {
        ...branch,
        address: {
          ...branch.address,
          country: branch.address?.country || defaultCountry,
        },
      };

      return branchApi
        .createBranch(branch.organisation, branchWithCountry)
        .pipe(
          tap((res) => {
            branches.update((branches) => [...branches, res]);
          }),
          finalize(() => patch({ loading: false })),
        );
    },
    updateBranch: (orgId: string, branchId: string, branch: BranchUpdate) => {
      patch({ loading: true });
      return branchApi.updateBranch(orgId, branchId, branch).pipe(
        tap((res) => {
          branches.update((branches) =>
            branches.map((b) => (b.id === branchId ? res : b)),
          );
        }),
        finalize(() => patch({ loading: false })),
      );
    },
    deleteBranch: (orgId: string, branchId: string) => {
      patch({ loading: true });
      return branchApi.deleteBranch(orgId, branchId).pipe(
        tap(() => {
          branches.update((branches) =>
            branches.filter((b) => b.id !== branchId),
          );
        }),
        finalize(() => patch({ loading: false })),
      );
    },
    generateQRCode: (orgId: string, branchId: string) => {
      return branchApi.generateQRCode(orgId, branchId).pipe(
        tap((result) => {
          // Update the branch with the new QR code URL
          branches.update((branches) =>
            branches.map((branch) =>
              branch.id === branchId
                ? { ...branch, qrcode: result.qrCodeUrl }
                : branch,
            ),
          );
        }),
      );
    },
  };
});
